import { hasFocusClass } from "@repo/fika-blog/src/classes"
import { extensions } from "@repo/fika-blog/src/extensions"
import type { Editor, JSONContent } from "@repo/fika-blog/src/tiptap/core"
import CharacterCount from "@tiptap/extension-character-count"
import Focus from "@tiptap/extension-focus"
import { Placeholder, TrailingNode } from "@tiptap/extensions"
import { isDeepEqual } from "remeda"
import {
  type Accessor,
  createEffect,
  createMemo,
  createSignal,
  on,
  untrack,
} from "solid-js"
import { createTiptapEditor } from "solid-tiptap"
import { Mini } from "@/designSystem/components/mini/mini"
import { useI18nContext } from "@/i18n/i18n-solid"
import { defaultJsonContent } from "@/lib/default-json-content"
import { useFormatToolbar } from "@/pages/posts/editor/menus/use-format-toolbar"
import { editorClass } from "@/pages/posts/editor/simple-wysiwyg.css"
import { dropCursorClass } from "@/pages/posts/editor/tiptap-editor.css"
import { noteEditorClass, noteToolbarClass } from "./note-editor.css"

interface CreateNoteEditorProps {
  content: Accessor<JSONContent | null>
  onUpdate?: (editor: Editor) => void
  placeholder?: string
  editable?: boolean
}

export function createNoteEditor(props: CreateNoteEditorProps) {
  const { LL } = useI18nContext()
  const [editorRef, setEditorRef] = createSignal<HTMLDivElement>()

  const editor = createTiptapEditor(() => ({
    element: editorRef()!,
    content: untrack(() => props.content?.()),
    autofocus: false,
    onUpdate: ({ editor }) => {
      props.onUpdate?.(editor)
    },
    editable: props.editable ?? true,
    editorProps: {
      attributes: {
        class: `${editorClass} ${noteEditorClass}`,
      },
    },
    extensions: [
      ...extensions({
        dropcursor: {
          color: false,
          width: 2,
          class: dropCursorClass,
        },
      }),
      Placeholder.configure({
        placeholder: props.placeholder ?? LL().editor.defaultPlaceholder(),
      }),
      Focus.configure({ className: hasFocusClass, mode: "deepest" }),
      CharacterCount,
      TrailingNode,
    ],
  }))

  return { editor, setEditorRef }
}

interface NoteToolbarProps {
  editor: Editor
}

export function NoteToolbar(props: NoteToolbarProps) {
  const { markValues, markOnChange } = useFormatToolbar({
    editor: () => props.editor,
  })

  return (
    <Mini.Menu ghost class={noteToolbarClass}>
      <Mini.ToggleGroupMultiple
        value={markValues()}
        onChange={markOnChange}
        values={[
          { value: "bold", iconName: "bold", title: "Bold" },
          { value: "italic", iconName: "italic", title: "Italic" },
          { value: "highlight", iconName: "highlighter", title: "Highlight" },
        ]}
      />
      <Mini.Divider />
      <Mini.Button
        iconName="list"
        onClick={() => props.editor.chain().focus().toggleBulletList().run()}
      />
      <Mini.Button
        iconName="quote"
        onClick={() => props.editor.chain().focus().toggleBlockquote().run()}
      />
    </Mini.Menu>
  )
}

interface NoteContentProps {
  content: string | JSONContent
}

export function NoteContent(props: NoteContentProps) {
  const parsedContent = createMemo<JSONContent>(
    () => {
      if (typeof props.content === "string") {
        try {
          return JSON.parse(props.content)
        } catch {
          return JSON.parse(defaultJsonContent)
        }
      }
      return props.content
    },
    {},
    { equals: isDeepEqual },
  )

  const { editor, setEditorRef } = createNoteEditor({
    content: () => untrack(parsedContent),
    editable: false,
  })

  createEffect(
    on(
      parsedContent,
      content => {
        editor()?.commands.setContent(content)
      },
      { defer: true },
    ),
  )

  return <div ref={setEditorRef} />
}
